import { useState } from "react";
import { BadgeDollarSign, ShieldCheck, Truck, TrendingUp, Building2, ArrowRight } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { WaIcon } from "./Logo";
import { waOpen } from "../lib/site";
import { useContent } from "../lib/content";

const PITCH = [
    {
        icon: BadgeDollarSign,
        title: "R$ 0 de custo",
        text: "A Conbox instala, opera e mantém todo o mercado — estrutura, estoque e limpeza. Sem investimento do condomínio.",
    },
    {
        icon: ShieldCheck,
        title: "Segurança de ponta a ponta",
        text: "Acesso controlado por QR Code ou cartão do condomínio e monitoramento por câmeras 24 horas.",
    },
    {
        icon: Truck,
        title: "Reposição e limpeza inclusas",
        text: "Equipe própria da Conbox abastece o estoque e cuida da conservação. O síndico não gerencia nada.",
    },
    {
        icon: TrendingUp,
        title: "Comodidade e valorização",
        text: "Um mercado 24h na entrada é o diferencial que os moradores amam e valoriza o condomínio.",
    },
];

const formatFor = (u) =>
    u <= 100
        ? "Loja Compacta (contêiner ou espaço cedido)"
        : u <= 300
          ? "Loja Completa (contêiner ou espaço cedido)"
          : "Projeto com múltiplas unidades";

export default function ParaSindicos() {
    const [units, setUnits] = useState(120);
    const t = useContent().sindicos;
    const rec = formatFor(units);

    const dossierMsg = `Olá! Quero receber a proposta completa de implantação do Conbox para um condomínio de ${units} unidade(s) — formato recomendado: ${rec}. Podemos agendar uma apresentação?`;

    return (
        <section
            id="sindicos"
            data-testid="sindico-pitch-container"
            className="scroll-mt-24 bg-[#1A231E] text-[#F7F4EC] py-20 sm:py-28 relative overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute top-8 right-0 select-none text-[15vw] font-black lowercase leading-none tracking-tighter text-outline whitespace-nowrap"
            >
                síndico
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-6 relative">
                <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-start">
                    <div>
                        <Reveal>
                            <SectionTag tone="beige">Para síndicos e conselheiros</SectionTag>
                            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                                {t.title1}
                                <span className="font-caveat text-[#F1C75B] font-bold text-4xl sm:text-5xl inline-block -rotate-1">
                                    {t.title2}
                                </span>
                            </h2>
                            <p className="mt-5 text-base sm:text-lg text-[#F7F4EC]/80 leading-relaxed max-w-xl">
                                {t.subtitle}
                            </p>
                        </Reveal>

                        <ul className="mt-9 grid sm:grid-cols-2 gap-4">
                            {PITCH.map((p, i) => (
                                <Reveal key={p.title} delay={i * 0.08}>
                                    <li className="h-full rounded-2xl border-2 border-[#F7F4EC]/15 bg-[#F7F4EC]/5 p-6 hover:bg-[#F7F4EC]/10 hover:-translate-y-1 transition-all duration-300">
                                        <p.icon className="w-8 h-8 text-[#F57A3A]" strokeWidth={2} />
                                        <h3 className="mt-4 text-lg font-extrabold">{t.pitches[i]?.t || p.title}</h3>
                                        <p className="mt-2 text-sm text-[#F7F4EC]/75 leading-relaxed">
                                            {t.pitches[i]?.d || p.text}
                                        </p>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                    </div>

                    <Reveal delay={0.1}>
                        <div className="rounded-[2rem] bg-[#F7F4EC] text-[#1A231E] p-7 sm:p-9 sticker rotate-1">
                            <div className="flex items-center gap-3">
                                <Building2 className="w-8 h-8 text-[#0E6B3F]" />
                                <h3 className="text-2xl font-extrabold">{t.sim_title}</h3>
                            </div>
                            <p className="mt-3 text-[#1A231E]/70 leading-relaxed">
                                Informe o número de unidades e veja o formato recomendado da loja:
                            </p>

                            <label
                                htmlFor="sindico-units"
                                className="mt-6 block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60"
                            >
                                Número de unidades
                            </label>
                            <input
                                id="sindico-units"
                                data-testid="sindico-calc-units-input"
                                type="number"
                                min="4"
                                max="2000"
                                value={units}
                                onChange={(e) =>
                                    setUnits(Math.max(0, Math.min(2000, Number(e.target.value))))
                                }
                                className="mt-2 w-full rounded-xl border-2 border-[#1A231E]/15 bg-white px-4 py-3.5 text-lg font-bold text-[#1A231E] focus:outline-none focus:border-[#F57A3A] transition-colors"
                            />

                            <div
                                data-testid="sindico-calc-output-badge"
                                className="mt-5 rounded-2xl bg-[#0E6B3F] text-[#F7F4EC] px-5 py-4"
                            >
                                <span className="block text-xs font-bold uppercase tracking-[0.16em] text-[#F1C75B]">
                                    Formato recomendado
                                </span>
                                <span className="block mt-1 text-xl font-extrabold">{rec}</span>
                                <span className="block mt-1 text-sm text-[#F7F4EC]/85">
                                    com R$ 0 de custo para o condomínio
                                </span>
                            </div>

                            <button
                                data-testid="sindico-cta-dossier-whatsapp"
                                onClick={() => waOpen(dossierMsg)}
                                className="mt-6 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] hover:bg-[#1FB457] text-[#073A22] font-extrabold px-6 py-4 text-base transition-all duration-200 hover:-translate-y-0.5"
                            >
                                <WaIcon className="w-5 h-5" />
                                Receber proposta no WhatsApp
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <p className="mt-3 text-center text-xs text-[#1A231E]/50 font-semibold">
                                {t.sim_note}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
